import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { EcosystemModuleAsync } from '@atlassian/jira-connect-utils-async/src/async.tsx';
import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { ExperienceSuccess } from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import { FlagRenderer } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import type { connectApps_softwareReleasesVersionDetail$key } from '@atlassian/jira-relay/src/__generated__/connectApps_softwareReleasesVersionDetail.graphql';
import { CONNECT_APP_EXPERIENCE_ID } from './constants.tsx';
import messages from './messages.tsx';
import { useLoadConnectJs, useConnectAppExtensionExperienceStart } from './utils.tsx';

type Props = {
	jiraVersion: connectApps_softwareReleasesVersionDetail$key;
};

export const ConnectApps = ({ jiraVersion }: Props) => {
	const { connectAddonIframeData } = useFragment(
		graphql`
			fragment connectApps_softwareReleasesVersionDetail on JiraVersion {
				connectAddonIframeData @required(action: THROW) {
					appKey @required(action: THROW)
					moduleKey @required(action: THROW)
					appName @required(action: THROW)
					location @required(action: THROW)
					options
				}
			}
		`,
		jiraVersion,
	);
	const { formatMessage } = useIntl();
	const hasConnectApps = connectAddonIframeData.length > 0;
	useConnectAppExtensionExperienceStart(hasConnectApps);
	const { loading, error } = useLoadConnectJs(hasConnectApps);
	const onExpFail = useExperienceFail({
		experience: CONNECT_APP_EXPERIENCE_ID,
		attributes: {
			errorMessage: error?.message ?? null,
			numConnectApps: connectAddonIframeData.length,
		},
	});

	if (!hasConnectApps || loading) {
		return null;
	}

	if (error) {
		onExpFail('versionDetailsHeader', error);
		// This should be caught by the nearest NonCriticalErrorBoundary
		throw error;
	}

	return (
		<>
			<ExperienceSuccess experience={CONNECT_APP_EXPERIENCE_ID} />
			{connectAddonIframeData
				.filter((iframeData) => iframeData != null && iframeData.options === null)
				.map((iframeData) => {
					if (iframeData == null) return null;

					return (
						<FlagRenderer
							key={`error-flag-${iframeData.appName}`}
							flag={{
								type: 'error',
								title: formatMessage(messages.errorTitle, {
									appName: iframeData.appName,
								}),
								description: messages.errorDescription,
								messageId:
									'software-releases-version-detail.ui.header.connect-apps.flag.error.flag-renderer.error',
								messageType: 'transactional',
							}}
						/>
					);
				})}
			{connectAddonIframeData
				.filter((iframeData) => iframeData != null && iframeData.options !== null)
				.map((iframeData) => {
					if (iframeData == null || iframeData.options == null) return null;
					const { appKey, moduleKey, options, appName, location } = iframeData;

					return (
						<EcosystemModuleAsync
							appKey={appKey}
							moduleKey={moduleKey}
							// Temp code to validate if hostOrigin is the problem. In the legacy page, hostOrigin was the public URL, but broken version had internal URL

							// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
							options={{ ...options, hostOrigin: window.location.origin }}
							appName={appName}
							key={`${appKey}-${moduleKey}`}
							location={location}
						/>
					);
				})}
		</>
	);
};
